import { defineComponent, reactive, ref, readonly, SetupContext, watch } from '@vue/composition-api';
import vueRouter from '@/router/index';                                                                    // ルート情報取得
import { UsersList, TableHeader, OrganizationTree, OnColumns, ConfigIconCoordinate } from '@/shared/type'; // ユーザー一覧情報の型をimport
import SystemConst from '@/shared/system-const';                                                           // 定数定義
import CommonFunction from '@/shared/common-function';                                                     // 共通関数定義
import HttpStatus from 'http-status-codes';                                                                // HTTPステータスコード取得用
import CommonMessageDialog from '@/components/common/common-message-dialog.component.vue';                 // 共通メッセージダイアログコンポーネント
import CommonOnColumnsMenu from '@/components/common/common-on-columns-menu.component.vue';                // 共通列設定メニューコンポーネント
import { DataOptions } from 'vuetify';                                                                     // v-data-tableのupdateイベントの引数を利用するための型
import CommonTableFilter from '@/components/common/common-table-filter.component.vue';                     // フィルタ用共通コンポーネント
import { TableFilterDefs, TableFilters } from '@/components/common/common-table-filter.component';

// リアクティブな変数の型定義
export type reactiveData = {
    visibleTree: boolean;                                               // 組織ツリー表示・非表示
    windowSize: { x: number; y: number };                               // Windowサイズ
    searchMethod: string;                                               // 検索方法（組織ツリーのラジオボタン） 0: すべての組織 1: 選択した組織
    searchTree: string;                                                 // 組織ツリーの検索文字列
    userListItem: UsersList[];                                          // ユーザー一覧情報
    userDisplayTableHeaders: TableHeader[];                             // ユーザー一覧ヘッダ情報
    organizationTree: OrganizationTree[];                               // 組織ツリー
    orgNameItems: string[];                                             // 組織一覧情報（所属組織フィルタ検索元）
    total: number;                                                      // 全ページの件数
    isLoading: boolean;                                                 // テーブルがロード中: true ロード中でない: false
    outputBulkFileDialog: boolean;                                      // 一括登録ファイルダウンロードダイアログ
    outputExcelDialog: boolean;                                         // 一覧データダウンロードダイアログ
    isDownLoading: boolean;                                             // ファイルがダウンロード中: true ダウンロード中でない: false
    displayConfirmationDialog: boolean;                                 // Excel出力確認ダイアログ表示フラグ
    filterDefs: TableFilterDefs;                                        // フィルタ定義
    pageOptions: DataOptions;                                           // ページ情報・ソート情報
    filters: TableFilters;                                              // フィルタ情報
    isFromAdd: boolean;                                                 // 登録・編集・削除画面からの遷移フラグ
};

export default defineComponent({
    components: { CommonMessageDialog, CommonOnColumnsMenu, CommonTableFilter },
    setup(props, ctx: SetupContext) {
        const router = vueRouter;                                       // ルータ
        const linesPerPage = SystemConst.LIST_LINES_PER_PAGE;           // 表示件数定義
        // リアクティブな変数定義
        const state: reactiveData = reactive<reactiveData>({
            visibleTree: false,                                         // 組織ツリー表示・非表示
            windowSize: { x: 0, y: 0 },                                 // ウィンドウサイズ
            searchMethod: SystemConst.USER_SEARCH_METHOD.ALL_USER,      // 検索方法（組織ツリーのラジオボタン）
            searchTree: '',                                             // 組織ツリーの検索文字列
            userListItem: [],                                           // ユーザー一覧情報
            userDisplayTableHeaders: [],                                // ユーザー一覧ヘッダー
            organizationTree: [],                                       // 組織ツリー
            orgNameItems: [],                                           // 組織一覧情報（所属組織フィルタ検索元）
            total: 0,                                                   // 全ページの件数
            isLoading: false,                                           // テーブルがロード中: true ロード中でない: false
            displayConfirmationDialog: true,                            // Excel出力確認ダイアログ表示フラグ
            outputBulkFileDialog: false,                                // 一括登録ファイルダウンロードダイアログ
            outputExcelDialog: false,                                   // 一覧データダウンロードダイアログ
            isDownLoading: false,                                       // ファイルがダウンロード中: true ダウンロード中でない: false
            filterDefs: {                                               // フィルタ定義
                'userId': { title: 'ユーザーID', types: 'KeyWordPartialMatch' },
                'identificationCode': { title: '識別コード', types: 'KeyWordPartialMatch' },
                'accountDivision': { title: 'アカウント区分', types: 'CheckBox' },
                'userName': { title: '氏名', types: 'KeyWordPartialMatch' },
                'userNameKana': { title: '氏名（かな）', types: 'KeyWordPartialMatch' },
                'organization': { title: '所属組織', types: 'PullDown' },
                'mail': { title: 'メールアドレス', types: 'KeyWordPartialMatch' },
                'postName': { title: '職', types: 'KeyWordPartialMatch' },
                'fiscalYear': { title: '作成年度', types: 'PullDown' },
                'isEnabled': { title: '状態', types: 'CheckBox' },
                'createdAt': { title: '登録日時', types: 'DatePeriod' },
                'updatedAt': { title: '更新日時', types: 'DatePeriod' }
            },
            pageOptions: {                                              // ページ情報・ソート情報
                page: 1, itemsPerPage: linesPerPage[1], sortBy: [], sortDesc: [],
                groupBy: [], groupDesc: [], multiSort: false, mustSort: false
            },
            filters: {},                                                // フィルタ情報
            isFromAdd: false                                            // 登録・編集・削除画面からの遷移フラグ
        });
        const tableLoadingText = SystemConst.TABLE_LOADING_TEXT;        // テーブルのデータ取得中に表示するテキスト
        const pageNation = ref<number>(1);                              // ページネーション(vuex管理用)
        const itemsPerPage = ref<number>(linesPerPage[1]);              // 表示件数(vuex管理用)
        const sortBy = ref<string[]>([]);                               // ソートカラム(vuex管理用)
        const sortDesc = ref<boolean[]>([]);                            // ソート昇順、降順(vuex管理用)
        const tableHeaderClass: string[] = ['secondary subtitle-2'];    // ユーザ一覧のヘッダ色
        const commonRef = ref<any>(null);                               // 共通ダイアログ
        const columnRef = ref<any>(null);                               // 列設定
        const child = ref<any>();                                       // テーブルフィルタ機能
        const treeIcons = SystemConst.TREE_ICON;                        // 組織ツリー用アイコン
        const orgTreeHeight = SystemConst.ORG_TREE_SIZE.HEIGHT;         // 組織ツリー高さ調整用変数
        const orgTreeWidth = SystemConst.ORG_TREE_SIZE.WIDTH;           // 組織ツリー幅調整用変数
        const tableHeight = SystemConst.TABLE_HEIGHT.USER_LIST;         // テーブル高さ調整用変数
        const activeNode = ref<any[]>([]);                              // 組織ツリーでactiveなノードを管理する変数
        const openNode = ref<any[]>([]);                                // 組織ツリーでノードを展開するかを管理する変数
        const tmpNode: any[] = [];                                      // 開いているノード情報を一時保存する変数
        // テーブルヘッダー（初期表示）
        const userInitialDisplayTableHeaders: TableHeader[] = [
            {
                text: '識別コード',
                value: 'identificationCode',
                width: '130px',
                class: tableHeaderClass
            },
            {
                text: 'アカウント区分',
                value: 'accountDivision',
                width: '180px',
                class: tableHeaderClass
            },
            {
                text: '氏名',
                value: 'userName',
                width: '130px',
                class: tableHeaderClass
            },
            {
                text: '所属組織',
                value: 'organization',
                width: '190px',
                class: tableHeaderClass
            },
            {
                text: 'メールアドレス',
                value: 'mail',
                width: '170px',
                class: tableHeaderClass
            },
            {
                text: '職',
                value: 'postName',
                width: '170px',
                class: tableHeaderClass
            },
            {
                text: '状態',
                value: 'isEnabled',
                width: '150px',
                class: tableHeaderClass
            },
            {
                text: '更新日時',
                value: 'updatedAt',
                width: '200px',
                class: tableHeaderClass
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];
        // テーブルヘッダー（全項目）
        const userTableHeaders: TableHeader[] = [
            {
                text: 'ユーザーID',
                value: 'userId',
                width: '130px',
                class: tableHeaderClass
            },
            {
                text: '識別コード',
                value: 'identificationCode',
                width: '130px',
                class: tableHeaderClass
            },
            {
                text: 'アカウント区分',
                value: 'accountDivision',
                width: '180px',
                class: tableHeaderClass
            },
            {
                text: '氏名',
                value: 'userName',
                width: '130px',
                class: tableHeaderClass
            },
            {
                text: '氏名（かな）',
                value: 'userNameKana',
                width: '180px',
                class: tableHeaderClass
            },
            {
                text: '所属組織',
                value: 'organization',
                width: '190px',
                class: tableHeaderClass
            },
            {
                text: 'メールアドレス',
                value: 'mail',
                width: '170px',
                class: tableHeaderClass
            },
            {
                text: '職',
                value: 'postName',
                width: '170px',
                class: tableHeaderClass
            },
            {
                text: '作成年度',
                value: 'fiscalYear',
                width: '170px',
                class: tableHeaderClass
            },
            {
                text: '状態',
                value: 'isEnabled',
                width: '150px',
                class: tableHeaderClass
            },
            {
                text: '登録日時',
                value: 'createdAt',
                width: '200px',
                class: tableHeaderClass
            },
            {
                text: '更新日時',
                value: 'updatedAt',
                width: '200px',
                class: tableHeaderClass
            },
            // 行アイコン
            {
                text: '',
                value: 'icons',
                sortable: false,
                width: '4%',
                class: tableHeaderClass,
                align: 'end'
            }
        ];
        // 列設定
        const onColumns: OnColumns[] = [
            {
                text: '全て表示',
                value: false
            },
            {
                text: 'ユーザーID',
                value: false
            },
            {
                text: '識別コード',
                value: true
            },
            {
                text: 'アカウント区分',
                value: true
            },
            {
                text: '氏名',
                value: true
            },
            {
                text: '氏名（かな）',
                value: false
            },
            {
                text: '所属組織',
                value: true
            },
            {
                text: 'メールアドレス',
                value: true
            },
            {
                text: '職',
                value: true
            },
            {
                text: '作成年度',
                value: false
            },
            {
                text: '状態',
                value: true
            },
            {
                text: '登録日時',
                value: false
            },
            {
                text: '更新日時',
                value: true
            }
        ];
        // ユーザーID
        const userIdRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, 'ユーザーID')
        ];
        // 識別コード
        const identificationCodeRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '識別コード')
        ];
        // 氏名
        const nameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '氏名')
        ];
        // 氏名（かな）
        const nameKanaRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '氏名（かな）')
        ];
        // メールアドレス
        const mailRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, 'メールアドレス'),
        ];
        // 職
        const postNameRules = [
            (v: any) => CommonFunction.checkLength(v, maxLength, '職')
        ];
        // フィルターに設定された最大桁数
        const maxLength = SystemConst.FILTER_MAX_LENGTH.FILTER_LENGTH;

        /**
         * created
         */
        created();
        async function created() {
            // titleIcon変更
            ctx.root.$store.commit('setTitleIcon', 'mdi-format-list-bulleted');

            // mainTitle変更
            ctx.root.$store.commit('setMainTitle', 'ユーザー一覧');

            // ユーザー一覧情報をクリア
            state.userListItem = [];

            // 初期表示ヘッダ設定
            state.userDisplayTableHeaders = userInitialDisplayTableHeaders;

            // storeからフィルター情報を復元する
            state.filters = ctx.root.$store.state.user.filters;

            // storeからページ情報を復元する
            pageNation.value = ctx.root.$store.state.user.pageNation;
            itemsPerPage.value = ctx.root.$store.state.user.itemsPerPage;

            // storeからソート情報を復元する
            sortBy.value = ctx.root.$store.state.user.sortBy;
            sortDesc.value = ctx.root.$store.state.user.sortDesc;

            // storeから登録等画面からの遷移フラグを復元する
            state.isFromAdd = ctx.root.$store.state.user.isFromAdd;

            // フィルタ初期設定
            initFilter();

            // 組織ツリー情報を取得
            await getOrganizationTree();

            // ストアにopenNodeのデータを保持している場合, 復元する
            if (ctx.root.$store.state.user.openNode.length > 0) {
                openNode.value = [];
                openNode.value = ctx.root.$store.state.user.openNode;
            }

            // activeなノード、組織ツリーのラジオボタン、組織ツリーの検索文字列を復元する
            activeNode.value = ctx.root.$store.state.user.activeNode;
            tmpNode[0] = activeNode.value[0];
            state.searchMethod = ctx.root.$store.state.user.searchMethod;
            state.searchTree = ctx.root.$store.state.user.searchTree;

            // vuexにフィルタ情報が保持されているデータを復元する
            const filterIdList = [];
            const filterParameters = [];
            for (const key in state.filters) {
                if (Object.prototype.hasOwnProperty.call(state.filters, key)) {
                    const val = state.filters[key];
                    if (val.isEnabled) {
                        // 有効なフィルタのIDを保存する
                        filterIdList.push(key);
                        // 有効なフィルタ条件を保存する
                        filterParameters.push(val.condition);
                    }
                }
            }
            if (filterIdList.length !== 0) {
                // 子コンポーネントのsetAllFiltersを呼び出し、フィルターを表示する(フィルタ更新処理から一覧が取得できる)
                child.value.setAllFilters(filterIdList, filterParameters);
            } else {
                // ユーザー一覧情報取得
                getUsers();
            }

            // 登録等画面からの遷移フラグにfalseを設定する
            state.isFromAdd = false;
        }

        /**
         * ウィンドウサイズ変更監視
         * 変更されたウィンドウサイズを変数に保持する
         */
        function onResize(): void {
            state.windowSize = { x: window.innerWidth, y: window.innerHeight };
        }

        /**
         * ユーザ登録画面へ遷移する
         */
        function navigationAdd() {
            // ユーザー登録画面へ遷移する
            router.push('/users/add');
        }

        /**
         * ユーザー編集画面へ遷移する
         * @param userId ユーザーID
         */
        function navigationUpdate(userId: string) {
            // ユーザーIDをパスに付与して画面遷移する
            router.push('/users/edit/' + userId);
        }

        /**
         * ユーザー削除画面へ遷移する
         * @param userId ユーザーID
         */
        function navigationDelete(userId: string) {
            // ユーザーIDをパスに付与して画面遷移する
            router.push('/users/delete/' + userId);
        }

        /**
         * ユーザー一覧取得処理
         */
        async function getUsers() {
            // ローディング
            state.isLoading = true;

            // 組織ID及び組織区分格納用変数
            let selectTreeId = '';
            let selectTreeDivision = SystemConst.SELECT_TREE_DIVISION['ALL'];

            // activeな組織が存在する場合、組織のID情報、区分情報を取得
            if (activeNode.value.length > 0) {
                const treeData = activeNode.value[0].split(',');
                selectTreeId = treeData[1];
                selectTreeDivision = treeData[0];
            }

            // ストアにパラメータを格納
            ctx.root.$store.commit('user/setPageNation', pageNation.value);
            ctx.root.$store.commit('user/setItemsPerPage', itemsPerPage.value);
            ctx.root.$store.commit('user/setOpenNode', openNode.value);
            ctx.root.$store.commit('user/setActiveNode', activeNode.value);
            ctx.root.$store.commit('user/setSearchMethod', state.searchMethod);
            ctx.root.$store.commit('user/setSearchTree', state.searchTree);
            ctx.root.$store.commit('user/setFilters', state.filters);
            ctx.root.$store.commit('user/setSortBy', sortBy.value);
            ctx.root.$store.commit('user/setSortDesc', sortDesc.value);

            // ページ情報・ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // アカウント区分フィルタで「教職員（組織管理者）」が選択されている場合、フィルタパラメータを調整する（paramsは参照渡し）
            CommonFunction.setOrgManagerFilterParams(params, state.filters);

            // 一覧表示用ユーザー情報取得APIを呼び出す
            const response = await ctx.root.$store.dispatch('user/getUsers',
                {
                    page: params['page'],                  // 作成したページ番号パラメータ
                    itemsPerPage: params['itemsPerPage'],  // 作成した1ページあたりの行数パラメータ
                    sortKey: params['sortKey'],            // 作成したソートキーパラメータ
                    sortOrder: params['sortOrder'],        // 作成したソート順パラメータ
                    filterParams: params['filterParams'],  // 作成したフィルタ情報パラメータ
                    selectTreeId,                           // 組織ツリーの組織ID
                    selectTreeDivision                      // 組織ツリーの組織区分
                }
            );

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // ローディング解除
                state.isLoading = false;

                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // ユーザー一覧情報 及び 全ページの件数の更新
            state.userListItem = response.data.users;
            state.total = response.data.total;

            // ローディング解除
            state.isLoading = false;
        }

        /**
         * 組織ツリー取得処理
         */
        async function getOrganizationTree() {
            // 組織ツリー取得APIを呼び出し、$state.organizationTreeに格納する
            const response = await ctx.root.$store.dispatch('user/getTree');

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // 組織ツリーを設定
            state.organizationTree = response.data.organizationTree;

            // 「全組織」のノードをオープンする
            openNode.value.push(state.organizationTree[0].id);

        }
        /**
         * フィルタ初期設定
         * 
         * フィルタにDBから取得する必要のある条件がある場合、初期表示で取得する。
         */
        async function initFilter() {
            // フィルタ条件の内容を取得
            // getFilterConditionsを呼び出す
            const response = await ctx.root.$store.dispatch('user/getFilterConditions');

            // API通信が正常でない場合
            if (response.status !== HttpStatus.OK) {
                // エラー画面を表示して処理を終了する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }

            // フィルタ（プルダウン・チェックボックス）に表示するデータを設定する
            // アカウント区分
            state.filterDefs['accountDivision'].chkItems = response.data.accountDivision;
            // 所属組織
            state.filterDefs['organization'].items = response.data.organizations;
            // 作成年度
            state.filterDefs['fiscalYear'].items = response.data.fiscalYear;
            // 状態
            state.filterDefs['isEnabled'].chkItems = response.data.isEnabled;

            // バリデーションを設定する
            // ユーザーID
            state.filterDefs['userId'].validator = userIdRules;
            // 識別コード
            state.filterDefs['identificationCode'].validator = identificationCodeRules;
            // 氏名
            state.filterDefs['userName'].validator = nameRules;
            // 氏名（かな）
            state.filterDefs['userNameKana'].validator = nameKanaRules;
            // メールアドレス
            state.filterDefs['mail'].validator = mailRules;
            // 職
            state.filterDefs['postName'].validator = postNameRules;
        }

        /**
         * フィルタ設定によるイベント
         * 
         * フィルタの条件を追加・削除したタイミングで呼ばれる。
         * このイベントでv-data-tableの表示更新を行うこと。
         * 
         * @param filters フィルタ条件
         */
        function updateFilter(filters: TableFilters) {
            // フィルタ条件をstate内へ保存する
            state.filters = filters;

            // 登録等画面からの遷移以外の場合
            if (!state.isFromAdd) {
                // 1ページへ戻す
                state.pageOptions.page = 1;
                pageNation.value = 1;
            }

            // ユーザー一覧再取得処理
            getUsers();
        }

        /**
         * 一覧のページ／ソート操作によるイベント
         * 
         * ページ操作とソート操作のタイミングで呼ばれる。
         * このイベントでv-data-tableの表示更新を行うこと。
         * 
         * @param options ページ／ソート情報
         */
        function updateOptions(options: DataOptions) {
            // ページ/ソート情報をstate内へ保存する
            state.pageOptions = options;

            if (pageNation.value === options.page &&
                itemsPerPage.value === options.itemsPerPage &&
                sortBy.value === options.sortBy &&
                sortDesc.value === options.sortDesc
            ) {
                // データの中身を比較して、いずれも変更されていなかった場合
                return;
            }

            // ページ/ソート情報を画面に適用する
            pageNation.value = options.page;
            itemsPerPage.value = options.itemsPerPage;
            sortBy.value = options.sortBy;
            sortDesc.value = options.sortDesc;
            // ユーザー一覧再取得処理
            getUsers();
        }

        /**
         * 組織ツリーでノードのactive情報が更新された際に発火する
         * 
         * @param node nodeオブジェクト
         */
        watch(activeNode, () => watchActiveNode());
        function watchActiveNode() {
            // 2回連続で同じactive時の組織だった場合、再度同じ条件で一覧取得をしない
            if (tmpNode[0] === activeNode.value[0]) {
                return;
            }

            // activeなノードが存在するか
            if (activeNode.value.length === 0) {
                // activeなノードが存在しない場合
                // 同じ組織をクリックした場合、activeでなくなるためラジオボタンを「すべての組織のユーザー」に切り替える
                state.searchMethod = SystemConst.USER_SEARCH_METHOD.ALL_USER;
            } else if (activeNode.value.length > 0) {
                // activeなノードが存在する場合
                // 組織ツリーのラジオボタンを「選択した組織のユーザー」に切り替える
                state.searchMethod = SystemConst.USER_SEARCH_METHOD.SELECT_USER;
            }
            // page情報の初期化
            state.pageOptions.page = 1;
            pageNation.value = 1;

            // アクティブなノード情報を保存する
            tmpNode[0] = activeNode.value[0];

            // ユーザー一覧情報取得
            getUsers();
        }

        /**
         * 検索方法（組織ツリーのラジオボタン）を手動で変更時の処理
         */
        function changeSearchMethod() {
            if (state.searchMethod === SystemConst.USER_SEARCH_METHOD.ALL_USER) {
                // すべての組織のユーザーが選択された場合
                // アクティブなノードをクリアする
                activeNode.value = [];
            } else if (state.searchMethod === SystemConst.USER_SEARCH_METHOD.SELECT_USER) {
                // 選択した組織のユーザーが選択された場合
                // 「全組織」のノードをactive状態にする
                activeNode.value = [];
                activeNode.value[0] = state.organizationTree[0].id;
            }

            // 全組織のノードを保存する（一覧情報を複数回取得してしまうため)
            tmpNode[0] = state.organizationTree[0].id;
        }

        /**
         * 列設定アイコンをクリックした際に発火する
         * 列設定アイコンの座標を取得し、列設定メニューコンポーネントのshowMenuを発火する
         */
        function showMenu() {
            const configIconCoordinate: ConfigIconCoordinate = { x: null, y: null };
            configIconCoordinate.x = document.querySelector('#column');
            configIconCoordinate.y = document.querySelector('#column');

            columnRef.value.showMenu(configIconCoordinate);
        }

        /**
         * 列設定メニューで、列設定情報が更新された際に発火する
         * 設定後のテーブルヘッダー情報を取得する
         * 
         * @param displayTableHeaders 画面に表示するテーブルヘッダー情報
         */
        function getTableHeader(displayTableHeaders: TableHeader[]) {
            state.userDisplayTableHeaders = displayTableHeaders;
        }

        /**
         * 一括登録用ファイルのダウンロード
         */
        async function outputBulkFile() {
            // 確認ダイアログを非表示にする
            state.displayConfirmationDialog = false;

            // ファイルダウンロード中にローダーを表示する
            state.isDownLoading = true;

            // 組織ID及び組織検索条件格納用変数
            let selectTreeId = '';
            let selectTreeDivision = SystemConst.SELECT_TREE_DIVISION['ALL'];

            // Excelファイル名格納
            const fileName = SystemConst.EXCEL_API_PARAM.BULK_ADD_USERS_FILE;

            // 一括登録用のファイルか（true:1, false:0）
            const isBulk = 1;

            // activeな組織が存在する場合、組織のID情報、組織検索条件を取得
            if (activeNode.value.length > 0) {
                const treeData = activeNode.value[0].split(',');
                selectTreeId = treeData[1];
                selectTreeDivision = treeData[0];
            }

            // ページ情報・ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // アカウント区分フィルタで「教職員（組織管理者）」が選択されている場合、フィルタパラメータを調整する（paramsは参照渡し）
            CommonFunction.setOrgManagerFilterParams(params, state.filters);

            // ユーザーstoreのoutputExcelを呼び出す
            // パラメータ:フィルタ・ソート情報
            //            組織ツリー情報
            //            Excelファイル名
            //            一括登録用のファイルか
            const response = await ctx.root.$store.dispatch('user/outputExcel',
                {
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams'],
                    selectTreeId,
                    selectTreeDivision,
                    fileName,
                    isBulk
                });

            // ファイルダウンロード中表示するローダーを非表示にする
            state.isDownLoading = false;

            // 一括登録用ダイアログの表示をfalseにする
            state.outputBulkFileDialog = false;

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }
        }

        /**
         * Excelファイルのダウンロード
         */
        async function outputExcelFile() {
            // 確認ダイアログを非表示にする
            state.displayConfirmationDialog = false;

            // ファイルダウンロード中にローダーを表示する
            state.isDownLoading = true;

            // 組織ID及び組織検索条件格納用変数
            let selectTreeId = '';
            let selectTreeDivision = SystemConst.SELECT_TREE_DIVISION['ALL'];

            // Excelファイル名格納
            const fileName = SystemConst.EXCEL_API_PARAM.USERS_FILE;

            // 一括登録用のファイルか（true:1, false:0）
            const isBulk = 0;

            // activeな組織が存在する場合、組織のID情報、組織検索条件を取得
            if (activeNode.value.length > 0) {
                const treeData = activeNode.value[0].split(',');
                selectTreeId = treeData[1];
                selectTreeDivision = treeData[0];
            }

            // ページ情報・ソート情報、及びフィルタ条件からAPIのパラメータを作成
            const params = CommonFunction.makeListParameters(state.pageOptions, state.filters);

            // アカウント区分フィルタで「教職員（組織管理者）」が選択されている場合、フィルタパラメータを調整する（paramsは参照渡し）
            CommonFunction.setOrgManagerFilterParams(params, state.filters);

            // ユーザーstoreのoutputExcelを呼び出す
            // パラメータ:フィルタ・ソート情報
            //            組織ツリー情報
            //            Excelファイル名
            //            一括登録用のファイルか
            const response = await ctx.root.$store.dispatch('user/outputExcel',
                {
                    sortKey: params['sortKey'],
                    sortOrder: params['sortOrder'],
                    filterParams: params['filterParams'],
                    selectTreeId,
                    selectTreeDivision,
                    fileName,
                    isBulk
                });

            // ファイルダウンロード中表示するローダーを非表示にする
            state.isDownLoading = false;

            // Excel出力用ダイアログの表示をfalseにする
            state.outputExcelDialog = false;

            // API通信が正常か
            if (response.status !== HttpStatus.OK) {
                // 正常でない場合エラー画面を表示する
                commonRef.value.errorDisplayTransitionControl(response);

                return;
            }
        }

        return {
            state: readonly(state),
            pageNation,
            sortBy,
            sortDesc,
            itemsPerPage,
            linesPerPage,
            treeIcons,
            activeNode,
            openNode,
            orgTreeHeight,
            orgTreeWidth,
            tableHeight,
            tableLoadingText,
            onColumns,
            child,
            commonRef,
            columnRef,
            userTableHeaders,
            userInitialDisplayTableHeaders,
            onResize,
            navigationAdd,
            navigationUpdate,
            navigationDelete,
            updateFilter,
            updateOptions,
            changeSearchMethod,
            showMenu,
            getTableHeader,
            outputBulkFile,
            outputExcelFile
        };
    }
});
